<template>
  <div v-loading="loading" class="nx-page nx-report-page emergency-plan-screen">
    <div class="eps-board">
      <div class="eps-title">
        <span>应急方案</span>
      </div>

      <div class="eps-actions">
        <el-input
          v-model="queryPlanId"
          clearable
          size="small"
          placeholder="预案ID"
          class="eps-plan-input"
          @keyup.enter.native="loadData"
        />
        <el-switch
          v-model="autoRefresh"
          active-text="刷新"
          inactive-text="手动"
        />
        <el-button size="small" icon="el-icon-refresh" @click="loadData" />
      </div>

      <el-empty v-if="!hasDisplayData && !loading" description="暂无应急预案大屏展示数据" />

      <template v-else>
        <section class="eps-panel eps-process">
          <div class="eps-process-line">
            <template v-for="(step, index) in processSteps">
              <div
                :key="`step-${index}`"
                :class="['eps-process-step', processStepClass(index)]"
              >
                <span>{{ step }}</span>
              </div>
              <i
                v-if="index < processSteps.length - 1"
                :key="`arrow-${index}`"
                class="eps-arrow"
              />
            </template>
          </div>
        </section>

        <section class="eps-panel eps-disposal">
          <div class="eps-panel-title">高频处置要点</div>
          <div class="eps-disposal-scroll">
            <div :class="['eps-disposal-track', { 'is-rolling': disposalList.length > 4 }]">
              <div
                v-for="(item, index) in doubledDisposalList"
                :key="`${item.riskType || 'risk'}-${index}`"
                class="eps-disposal-row"
              >
                <div class="eps-disposal-name">{{ item.riskType || `处置要点${index + 1}` }}</div>
                <div class="eps-disposal-text">{{ item.disposalSteps || item.content || '--' }}</div>
              </div>
            </div>
          </div>
        </section>

        <section class="eps-panel eps-organization">
          <div class="eps-panel-title">响应组织</div>
          <div v-if="deputyCommanderText" class="eps-deputy">
            <span>副总指挥</span>
            <strong>{{ deputyCommanderText }}</strong>
          </div>
          <div class="eps-org-scroll">
            <div :class="['eps-org-track', { 'is-rolling': professionalGroups.length > 3 }]">
              <div
                v-for="(item, index) in doubledProfessionalGroups"
                :key="`${item.roleCode || item.roleName || 'org'}-${index}`"
                class="eps-org-row"
              >
                <span>{{ item.roleName || item.roleCode || '未分组' }}</span>
                <b>{{ item.leaderName || '--' }}</b>
                <em>{{ item.dutyDesc || firstMemberDuty(item) || '--' }}</em>
              </div>
            </div>
          </div>
        </section>

        <div class="eps-duty-phone">24小时值班 {{ dutyPhone }}</div>
      </template>
    </div>
  </div>
</template>

<script>
import { fetchEmergencyPlanScreen } from './api'
import './index.scss'

const DEFAULT_PROCESS_STEPS = [
  '事故报警',
  '现场先期处置',
  '研判分级',
  '启动对应预案',
  '各组同步开展救援（疏散/抢险/急救/物资/对外联络）',
  '控制险情',
  '应急终止',
  '现场清理、事故调查、预案修订'
]

function getField(row, camel, snake) {
  if (!row) return ''
  return row[camel] !== undefined && row[camel] !== null ? row[camel] : row[snake]
}

function safeJson(value, fallback) {
  if (!value) return fallback
  if (typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch (e) {
    return fallback
  }
}

export default {
  name: 'EmergencyPlanScreen',
  data() {
    return {
      loading: false,
      autoRefresh: true,
      refreshTimer: null,
      queryPlanId: '',
      dashboard: {
        plan: null,
        file: null,
        contents: [],
        processSteps: [],
        supportOrganizations: []
      }
    }
  },
  computed: {
    contents() {
      return this.dashboard.contents || []
    },
    hasDisplayData() {
      return this.processSteps.length || this.disposalList.length || this.professionalGroups.length
    },
    subtitle() {
      return this.fileName ? `来源文件：${this.fileName}` : '预案解析内容与应急保障人员'
    },
    fileName() {
      return getField(this.dashboard.file, 'originalFileName', 'original_file_name') || ''
    },
    planTitle() {
      const plan = this.dashboard.plan || {}
      return plan.planName || plan.name || plan.title || '宁夏物流集团煤炭储备（中卫）应急预案'
    },
    planNo() {
      const plan = this.dashboard.plan || {}
      return plan.planCode || plan.planNo || plan.code || '应急预案'
    },
    fileStatusText() {
      const status = getField(this.dashboard.file, 'parseStatus', 'parse_status')
      return Number(status) === 2 ? '解析完成' : '展示中'
    },
    updateTimeText() {
      const file = this.dashboard.file || {}
      return getField(file, 'updateTime', 'update_time') || getField(file, 'createTime', 'create_time') || '--'
    },
    processSteps() {
      const steps = this.dashboard.processSteps || this.dashboard.process_steps
      return Array.isArray(steps) && steps.length ? steps : DEFAULT_PROCESS_STEPS
    },
    disposalList() {
      const payload = this.sectionPayload('disposal')
      if (Array.isArray(payload.items)) return payload.items
      if (Array.isArray(payload)) return payload
      return this.sectionItems('disposal').map(item => {
        const parts = item.split(/[：:]/)
        return {
          riskType: parts[0],
          disposalSteps: parts.slice(1).join('：') || item
        }
      })
    },
    doubledDisposalList() {
      return this.disposalList.length > 4
        ? this.disposalList.concat(this.disposalList)
        : this.disposalList
    },
    organizations() {
      return this.dashboard.supportOrganizations || this.dashboard.support_organizations || []
    },
    deputyCommanderText() {
      const names = []
      this.organizations.forEach(item => {
        const roleName = item.roleName || item.role_name || item.roleCode || item.role_code || ''
        if (!roleName.includes('副总指挥')) return
        const leader = item.leaderName || item.leader_name
        if (leader) names.push(leader)
        const members = item.members || []
        members.forEach(member => {
          if (member.name) names.push(member.name)
        })
      })
      return Array.from(new Set(names)).join('、')
    },
    professionalGroups() {
      return this.organizations.filter(item => {
        const roleName = item.roleName || item.role_name || item.roleCode || item.role_code || ''
        return !roleName.includes('总指挥')
      })
    },
    doubledProfessionalGroups() {
      return this.professionalGroups.length > 3
        ? this.professionalGroups.concat(this.professionalGroups)
        : this.professionalGroups
    },
    dutyPhone() {
      const text = this.sectionText('external_contact')
      const dutyMatch = text.match(/(?:24小时值班|值班电话|公司24小时值班)[^\d]*(1\d{10})/)
      if (dutyMatch) return dutyMatch[1]
      const phoneMatch = text.match(/1\d{10}/)
      return phoneMatch ? phoneMatch[0] : (this.dashboard.defaultDutyPhone || this.dashboard.default_duty_phone || '18695189950')
    }
  },
  watch: {
    autoRefresh(val) {
      this.resetRefreshTimer()
      if (val) this.startRefreshTimer()
    }
  },
  mounted() {
    this.queryPlanId = this.$route.query.planId || ''
    this.loadData()
    this.startRefreshTimer()
  },
  beforeDestroy() {
    this.clearRefreshTimer()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const params = {}
        if (this.queryPlanId) params.planId = this.queryPlanId
        const data = await fetchEmergencyPlanScreen(params)
        this.dashboard = data || { plan: null, file: null, contents: [], processSteps: [], supportOrganizations: [] }
      } finally {
        this.loading = false
      }
    },
    section(code) {
      return this.contents.find(item => getField(item, 'sectionCode', 'section_code') === code) || {}
    },
    sectionText(code) {
      return getField(this.section(code), 'contentText', 'content_text') || ''
    },
    sectionPayload(code) {
      return safeJson(getField(this.section(code), 'contentJson', 'content_json'), {})
    },
    sectionItems(code) {
      const payload = this.sectionPayload(code)
      if (Array.isArray(payload.items)) return payload.items
      if (Array.isArray(payload)) return payload
      const text = this.sectionText(code)
      return text ? text.split('\n').filter(Boolean) : []
    },
    firstMemberDuty(item) {
      const member = (item.members || []).find(row => row && row.dutyDesc)
      return member ? member.dutyDesc : ''
    },
    processStepClass(index) {
      if (index === 2 || index === 3) return 'is-warn'
      if (index === 5) return 'is-success'
      return ''
    },
    startRefreshTimer() {
      if (!this.autoRefresh) return
      this.refreshTimer = window.setInterval(() => this.loadData(), 60000)
    },
    resetRefreshTimer() {
      this.clearRefreshTimer()
      this.startRefreshTimer()
    },
    clearRefreshTimer() {
      if (this.refreshTimer) {
        window.clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    }
  }
}
</script>
